<template>
  <template v-if="label">
    <div
      v-if="!hasAnchor"
      class="nt-cmp-marker__preview nt-cmp-marker__preview--inline"
      role="status"
    >
      <span class="nt-cmp-marker__preview-tag">Personalized</span>
      <span v-if="audienceName" class="nt-cmp-marker__preview-detail">
        {{ audienceName }}
      </span>
      <span v-if="experienceName" class="nt-cmp-marker__preview-detail">
        {{ experienceName }}
      </span>
    </div>

    <Teleport to="body">
      <div
        v-if="hasAnchor && outlineStyle"
        class="nt-cmp-marker__outline"
        :style="outlineStyle"
        aria-hidden="true"
      />

      <div
        v-if="hasAnchor && badgeStyle"
        class="nt-cmp-marker__preview"
        :style="badgeStyle"
        role="status"
      >
        <span class="nt-cmp-marker__preview-tag">Personalized</span>
        <span v-if="audienceName" class="nt-cmp-marker__preview-detail">
          {{ audienceName }}
        </span>
        <span v-if="experienceName" class="nt-cmp-marker__preview-detail">
          {{ experienceName }}
        </span>
      </div>
    </Teleport>
  </template>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import type {
  ExperienceConfiguration,
  Reference,
  VariantRef
} from '@ninetailed/experience.js-shared';
import { useElementOverlayPosition } from '~/composables/ninetailed/useElementOverlayPosition';

const props = defineProps<{
  baseline?: Reference;
  experience?: ExperienceConfiguration<Reference | VariantRef> | null;
  target?: Element | null | Ref<Element | null | undefined>;
  variant: Reference | VariantRef;
}>();

const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();

const targetRef = computed(() => {
  const { target } = props;

  if (target && typeof target === 'object' && 'value' in target) {
    return target.value ?? null;
  }

  return target ?? null;
});

const { badgeStyle, hasAnchor, outlineStyle } =
  useElementOverlayPosition(targetRef);

const isPersonalizedVariant = computed(
  () => Boolean(props.baseline) && props.variant.id !== props.baseline?.id
);

const audienceName = computed(() => props.experience?.audience?.name ?? '');
const experienceName = computed(() => props.experience?.name ?? '');

const label = computed(() => {
  if (!isPreviewEnabled || !isPersonalizedVariant.value) {
    return '';
  }

  return [audienceName.value, experienceName.value].filter(Boolean).join(' / ');
});
</script>

<style scoped>
.nt-cmp-marker__preview {
  background-color: rgb(140 46 234 / 94%);
  border-radius: 0.25rem;
  box-shadow: 0 2px 10px rgb(0 0 0 / 18%);
  color: white;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.25rem 0.5rem;
  line-height: 1.125rem;
  max-width: min(18rem, calc(100vw - 1rem));
  padding: 0.25rem 0.5rem;
  pointer-events: none;
  position: fixed;
  z-index: 9999;
}

.nt-cmp-marker__preview--inline {
  margin: 0 0 0.5rem;
  position: static;
  width: fit-content;
}

.nt-cmp-marker__preview-tag {
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.nt-cmp-marker__preview-detail + .nt-cmp-marker__preview-detail::before {
  content: '·';
  margin-right: 0.5rem;
}

.nt-cmp-marker__outline {
  background: rgb(140 46 234 / 4%);
  border: 2px dashed #8c2eea;
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 35%);
  box-sizing: border-box;
  pointer-events: none;
  position: fixed;
  z-index: 9998;
}
</style>
